import React, { useEffect, useState, useRef } from 'react';
import projectAPI from '../api/projectAPI';
import workerAPI from '../api/workerAPI';
import {
  FaEdit, FaTrash, FaUserPlus, FaTimes, FaChevronRight, FaLink,
  FaUpload, FaFilePdf, FaFileWord, FaFileImage, FaFile,
  FaDownload, FaEye, FaSpinner, FaTrash as FaTrashIcon,
} from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import UserDashboard from './UserDashboard';

// ── Helpers ───────────────────────────────────────────────────────────────────
const ROLE_COLOR = {
  'Marketing':     'bg-pink-100 text-pink-700',
  'Project Leads': 'bg-indigo-100 text-indigo-700',
  'HR & Design':   'bg-teal-100 text-teal-700',
  'TL':            'bg-blue-100 text-blue-700',
  'Developer':     'bg-green-100 text-green-700',
  'Office':        'bg-gray-100 text-gray-600',
};
const RoleBadge = ({ role }) => (
  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_COLOR[role] || 'bg-gray-100 text-gray-600'}`}>{role}</span>
);
const ChainTrail = ({ chain }) => {
  if (!chain?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {chain.map((entry, i) => (
        <React.Fragment key={i}>
          <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full">{entry}</span>
          {i < chain.length - 1 && <FaChevronRight className="text-purple-300 text-xs" />}
        </React.Fragment>
      ))}
    </div>
  );
};

// ── File helpers ──────────────────────────────────────────────────────────────
const getFileType = (file) => {
  if (file.type.startsWith('image/'))  return 'image';
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type.includes('word') || file.name.endsWith('.doc') || file.name.endsWith('.docx')) return 'doc';
  return 'other';
};
const FileIcon = ({ fileType }) => {
  if (fileType === 'image') return <FaFileImage className="text-blue-500 text-xl shrink-0" />;
  if (fileType === 'pdf')   return <FaFilePdf   className="text-red-500 text-xl shrink-0" />;
  if (fileType === 'doc')   return <FaFileWord  className="text-blue-700 text-xl shrink-0" />;
  return <FaFile className="text-gray-500 text-xl shrink-0" />;
};
const fmtSize = (b) => b < 1024 ? `${b}B` : b < 1048576 ? `${(b/1024).toFixed(1)}KB` : `${(b/1048576).toFixed(1)}MB`;
const toBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

// ── Reusable FileUploadPanel ──────────────────────────────────────────────────
const FileUploadPanel = ({ projectId, existingFiles = [], onUploaded }) => {
  const [pending, setPending]       = useState([]);
  const [uploading, setUploading]   = useState(false);
  const [uploaded, setUploaded]     = useState(existingFiles);
  const [previewUrl, setPreviewUrl] = useState(null);
  const inputRef = useRef(null);

  const addFiles = (fileList) => setPending(prev => [...prev, ...Array.from(fileList).map(f => ({
    file: f, name: f.name, size: f.size, fileType: getFileType(f),
    preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
  }))]);

  const handleUpload = async () => {
    if (!projectId) { alert('Save project first.'); return; }
    if (!pending.length) { alert('No files selected.'); return; }
    setUploading(true);
    try {
      const files = await Promise.all(pending.map(async pf => ({
        base64: await toBase64(pf.file), name: pf.name, fileType: pf.fileType,
      })));
      const res = await projectAPI.post(`/${projectId}/attachments`, { files });
      setUploaded(res.data.attachments); setPending([]);
      onUploaded?.(res.data.attachments);
      alert(`✅ ${files.length} file(s) uploaded!`);
    } catch { alert('❌ Upload failed.'); } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await projectAPI.delete(`/${projectId}/attachments/${id}`);
      setUploaded(prev => prev.filter(f => f._id !== id));
    } catch { alert('❌ Delete failed.'); }
  };

  return (
    <div className="border border-purple-200 rounded-xl p-4 bg-purple-50 space-y-3">
      <h4 className="font-semibold text-purple-700 text-sm flex items-center gap-2">
        <FaUpload /> Attachments <span className="text-xs font-normal text-gray-500">(visible to all assignees)</span>
      </h4>
      <div onClick={() => inputRef.current?.click()} onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-purple-300 rounded-lg p-4 text-center cursor-pointer hover:bg-purple-100 transition">
        <FaUpload className="mx-auto text-purple-400 text-xl mb-1" />
        <p className="text-sm text-gray-500">Click or drag & drop files here</p>
        <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, PDF, DOC, DOCX</p>
        <input ref={inputRef} type="file" multiple
          accept="image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }} className="hidden" />
      </div>
      {pending.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase">Ready to upload</p>
          {pending.map((pf, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
              {pf.preview ? <img src={pf.preview} alt="" className="w-9 h-9 object-cover rounded" /> : <FileIcon fileType={pf.fileType} />}
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-700 truncate">{pf.name}</p><p className="text-xs text-gray-400">{fmtSize(pf.size)}</p></div>
              <button onClick={() => setPending(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><FaTrash /></button>
            </div>
          ))}
          <button onClick={handleUpload} disabled={uploading}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold py-2 rounded-lg flex items-center justify-center gap-2">
            {uploading ? <><FaSpinner className="animate-spin" /> Uploading...</> : <><FaUpload /> Upload {pending.length} file(s)</>}
          </button>
        </div>
      )}
      {uploaded.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase">Uploaded ({uploaded.length})</p>
          {uploaded.map(f => (
            <div key={f._id} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
              <FileIcon fileType={f.fileType} />
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-700 truncate">{f.name}</p><p className="text-xs text-gray-400">by {f.uploadedBy} · {new Date(f.uploadedAt).toLocaleDateString('en-GB')}</p></div>
              <div className="flex items-center gap-2">
                {f.fileType === 'image' && <button onClick={() => setPreviewUrl(f.url)} className="text-blue-500 hover:text-blue-700"><FaEye /></button>}
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-700"><FaDownload /></a>
                <button onClick={() => handleDelete(f._id)} className="text-red-400 hover:text-red-600"><FaTrash /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!uploaded.length && !pending.length && <p className="text-xs text-gray-400 text-center">No attachments yet.</p>}
      {previewUrl && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-80 flex items-center justify-center px-4" onClick={() => setPreviewUrl(null)}>
          <div className="relative max-w-3xl w-full">
            <button onClick={() => setPreviewUrl(null)} className="absolute -top-10 right-0 text-white text-2xl">✕</button>
            <img src={previewUrl} alt="preview" className="w-full rounded-xl" />
          </div>
        </div>
      )}
    </div>
  );
};

// ── Constants ─────────────────────────────────────────────────────────────────
// Roles that can CREATE projects
const CREATE_ROLES  = ['Office', 'Marketing', 'Project Leads'];
// Roles that can FORWARD projects
const FORWARD_ROLES = ['Office', 'Marketing', 'Project Leads', 'TL', 'HR & Design'];
// Admin panel detection
const ADMIN_ROLES   = ['Office', 'admin', undefined, null];

// ─────────────────────────────────────────────────────────────────────────────
const OfficeProjectManager = () => {
  const worker     = JSON.parse(localStorage.getItem('worker'));
  const workerRole = worker?.role;
  const workerId   = worker?._id;
  const isAdmin    = ADMIN_ROLES.includes(workerRole);

  const [projects, setProjects]         = useState([]);
  const [workers, setWorkers]           = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm]     = useState('');
  const [showChart, setShowChart]       = useState(false);
  const [statusFilter, setStatusFilter] = useState('All');

  // Admin form
  const [formData, setFormData]             = useState({ title: '', description: '', assignedTo: [], startDate: '', endDate: '', status: '' });
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [savedProjectId, setSavedProjectId]     = useState(null);
  const [savedProjectTitle, setSavedProjectTitle] = useState('');

  // Chain form (Marketing, PL)
  const [chainForm, setChainForm]               = useState({ title: '', description: '', startDate: '', endDate: '' });
  const [assignableWorkers, setAssignableWorkers] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [assigneeDropOpen, setAssigneeDropOpen]   = useState(false);

  // Forward modal
  const [showForwardModal, setShowForwardModal]     = useState(false);
  const [forwardProject, setForwardProject]         = useState(null);
  const [forwardWorkers, setForwardWorkers]         = useState([]);
  const [forwardSelectedIds, setForwardSelectedIds] = useState([]);

  // Chain modal
  const [showChainModal, setShowChainModal] = useState(false);
  const [chainProject, setChainProject]     = useState(null);

  // Attachment modal
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [attachProject, setAttachProject]     = useState(null);

  useEffect(() => {
    fetchProjects();
    if (isAdmin) fetchAllWorkers();
    if (CREATE_ROLES.includes(workerRole) && !isAdmin) fetchAssignableWorkers();
  }, []);

  const fetchProjects = async () => {
    try {
      const endpoint = isAdmin ? '/' : '/my-projects';
      const res = await projectAPI.get(endpoint);
      setProjects(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchAllWorkers = async () => {
    try { const res = await workerAPI.get('/'); setWorkers(res.data); }
    catch (err) { console.error(err); }
  };

  const fetchAssignableWorkers = async () => {
    try { const res = await projectAPI.get('/assignable-workers'); setAssignableWorkers(res.data); }
    catch (err) { console.error(err); }
  };

  // ── Admin form ────────────────────────────────────────────────────────────
  const handleCheckboxChange = (id) => {
    setFormData(prev => ({
      ...prev,
      assignedTo: prev.assignedTo.includes(id) ? prev.assignedTo.filter(x => x !== id) : [...prev.assignedTo, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProjectId) {
        await projectAPI.put(`/${editingProjectId}`, formData);
        setSavedProjectId(editingProjectId); setSavedProjectTitle(formData.title);
        alert('Project updated! You can now manage attachments below.');
        setEditingProjectId(null);
      } else {
        const res = await projectAPI.post('/', formData);
        setSavedProjectId(res.data._id); setSavedProjectTitle(res.data.title);
        alert('✅ Project saved! You can now upload attachments below.');
      }
      setFormData({ title: '', description: '', assignedTo: [], startDate: '', endDate: '', status: '' });
      setDropdownOpen(false);
      fetchProjects();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (project) => {
    setFormData({ title: project.title, description: project.description, assignedTo: project.assignedTo, startDate: project.startDate, endDate: project.endDate, status: project.status });
    setEditingProjectId(project._id);
    setSavedProjectId(project._id); setSavedProjectTitle(project.title);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try { await projectAPI.delete(`/${id}`); fetchProjects(); }
    catch (err) { console.error(err); }
  };

  // ── Chain form ────────────────────────────────────────────────────────────
  const handleChainSubmit = async (e) => {
    e.preventDefault();
    if (!chainForm.title || !chainForm.description) { alert('Fill all fields.'); return; }
    if (!selectedAssignees.length) { alert('Select at least one assignee.'); return; }
    try {
      const res = await projectAPI.post('/chain', { ...chainForm, assigneeIds: selectedAssignees });
      setSavedProjectId(res.data._id); setSavedProjectTitle(res.data.title);
      alert('✅ Project assigned! You can now upload attachments below.');
      setChainForm({ title: '', description: '', startDate: '', endDate: '' });
      setSelectedAssignees([]); setAssigneeDropOpen(false);
      fetchProjects();
    } catch (err) { alert(err.response?.data?.error || '❌ Failed to create project.'); }
  };

  // ── Forward ───────────────────────────────────────────────────────────────
  const openForwardModal = async (project) => {
    setForwardProject(project); setForwardSelectedIds([]);
    try {
      const res = await projectAPI.get('/assignable-workers');
      const existingIds = (project.assignees || []).map(a => a.userId?.toString());
      setForwardWorkers(res.data.filter(w => !existingIds.includes(w._id)));
    } catch (err) { console.error(err); }
    setShowForwardModal(true);
  };

  const handleForward = async () => {
    if (!forwardSelectedIds.length) { alert('Select at least one worker.'); return; }
    try {
      await projectAPI.post(`/${forwardProject._id}/forward`, { assigneeIds: forwardSelectedIds });
      alert('✅ Project forwarded!'); setShowForwardModal(false); fetchProjects();
    } catch (err) { alert(err.response?.data?.error || '❌ Forward failed.'); }
  };

  const chartData = [
    { status: 'Ongoing',   count: projects.filter(p => p.status === 'Ongoing').length },
    { status: 'Completed', count: projects.filter(p => p.status === 'Completed').length },
    { status: 'On Hold',   count: projects.filter(p => p.status === 'On Hold').length },
  ];

  const filtered = projects.filter(p =>
    p.title?.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (statusFilter === 'All' || p.status === statusFilter)
  );

  return (
    <div className="min-h-screen bg-purple-100 p-6 pt-20">
      <UserDashboard />

      {/* Header */}
      <div className="max-w-7xl mx-auto flex justify-between items-center mb-5 mt-4 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-mono text-purple-700">Project Tracking</h1>
          {worker && <p className="text-sm text-gray-500 mt-0.5">{worker.username} <RoleBadge role={workerRole} /></p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {['All','Ongoing','On Hold','Completed'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${statusFilter === s ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'}`}>{s}</button>
          ))}
          <button onClick={() => setShowChart(!showChart)} className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 text-sm">
            {showChart ? 'Back to Projects' : 'View Chart'}
          </button>
        </div>
      </div>

      {showChart ? (
        <div className="bg-white p-6 rounded-xl shadow max-w-4xl mx-auto mt-6">
          <h2 className="text-xl font-semibold text-purple-600 mb-4">Project Status Chart</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}><XAxis dataKey="status" /><YAxis allowDecimals={false} /><Tooltip /><Legend /><Bar dataKey="count" fill="#9F7AEA" /></BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* ── LEFT: Create Form ─────────────────────────────────────────── */}
          <div className="bg-white p-5 rounded-xl shadow overflow-y-auto max-h-[85vh]">

            {/* Admin / Office */}
            {isAdmin && (
              <>
                <h2 className="text-xl font-semibold mb-4 text-purple-600">{editingProjectId ? 'Edit Project' : 'Add New Project'}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Project Title</label>
                    <input type="text" name="title" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required className="border px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-purple-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea name="description" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} required className="border px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-purple-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                    <button type="button" onClick={() => setDropdownOpen(!dropdownOpen)}
                      className="border px-4 py-2 rounded-lg w-full text-left bg-white text-sm">
                      {formData.assignedTo.length === 0 ? 'Select workers' : `${formData.assignedTo.length} worker(s) selected`}
                    </button>
                    {dropdownOpen && (
                      <div className="mt-1 border rounded-lg bg-white shadow z-20 absolute max-h-40 overflow-y-auto">
                        {workers.filter(w => ['HR & Design','Marketing','Project Leads','TL','Developer'].includes(w.role)).map(w => (
                          <label key={w._id} className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm">
                            <input type="checkbox" checked={formData.assignedTo.includes(w._id)} onChange={() => handleCheckboxChange(w._id)} className="accent-purple-600" />
                            {w.username} <RoleBadge role={w.role} />
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <input type="date" name="startDate" value={formData.startDate} onChange={e => setFormData({...formData, startDate: e.target.value})} className="border px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-purple-500 outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                      <input type="date" name="endDate" value={formData.endDate} onChange={e => setFormData({...formData, endDate: e.target.value})} className="border px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-purple-500 outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select name="status" value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} required className="border px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-purple-500 outline-none">
                      <option value="" disabled hidden>Select Status</option>
                      <option value="Ongoing">Ongoing</option>
                      <option value="Completed">Completed</option>
                      <option value="On Hold">On Hold</option>
                    </select>
                  </div>
                  <button type="submit" className="bg-purple-500 text-white px-6 py-2 rounded-lg hover:bg-purple-600 w-full">
                    {editingProjectId ? 'Update Project' : 'Save Project'}
                  </button>
                </form>
                {savedProjectId && (
                  <div className="mt-3">
                    <p className="text-xs text-green-600 font-medium mb-2">✅ Files for: <span className="font-bold">{savedProjectTitle}</span></p>
                    <FileUploadPanel projectId={savedProjectId}
                      existingFiles={projects.find(p => p._id === savedProjectId)?.attachments || []}
                      onUploaded={(attachments) => setProjects(prev => prev.map(p => p._id === savedProjectId ? { ...p, attachments } : p))} />
                  </div>
                )}
              </>
            )}

            {/* Marketing / Project Leads chain form */}
            {!isAdmin && CREATE_ROLES.includes(workerRole) && (
              <>
                <h2 className="text-xl font-semibold mb-4 text-purple-600">Create & Assign Project</h2>
                <form onSubmit={handleChainSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Project Title</label>
                    <input type="text" value={chainForm.title} onChange={e => setChainForm({...chainForm, title: e.target.value})} required className="border px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-purple-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea rows={2} value={chainForm.description} onChange={e => setChainForm({...chainForm, description: e.target.value})} required className="border px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-purple-500 outline-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <input type="date" value={chainForm.startDate} onChange={e => setChainForm({...chainForm, startDate: e.target.value})} className="border px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-purple-500 outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                      <input type="date" value={chainForm.endDate} onChange={e => setChainForm({...chainForm, endDate: e.target.value})} className="border px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-purple-500 outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                    <button type="button" onClick={() => setAssigneeDropOpen(!assigneeDropOpen)}
                      className="border px-4 py-2 rounded-lg w-full text-left bg-white text-sm">
                      {selectedAssignees.length === 0 ? 'Select workers' : `${selectedAssignees.length} worker(s) selected`}
                    </button>
                    {assigneeDropOpen && (
                      <div className="mt-1 border rounded-lg bg-white shadow z-20 max-h-44 overflow-y-auto">
                        {assignableWorkers.length === 0 && <p className="text-sm text-gray-400 p-3">No assignable workers found.</p>}
                        {assignableWorkers.map(w => (
                          <label key={w._id} className={`flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-purple-50 text-sm ${selectedAssignees.includes(w._id) ? 'bg-purple-50' : ''}`}>
                            <input type="checkbox" checked={selectedAssignees.includes(w._id)}
                              onChange={() => setSelectedAssignees(prev => prev.includes(w._id) ? prev.filter(x => x !== w._id) : [...prev, w._id])}
                              className="accent-purple-600" />
                            <span className="flex-1">{w.username}</span>
                            <RoleBadge role={w.role} />
                          </label>
                        ))}
                      </div>
                    )}
                    {selectedAssignees.length > 0 && <p className="text-xs text-purple-600 mt-1">{selectedAssignees.length} selected</p>}
                  </div>
                  <button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg w-full font-semibold">
                    Assign Project
                  </button>
                </form>
                {savedProjectId && (
                  <div className="mt-3">
                    <p className="text-xs text-green-600 font-medium mb-2">✅ Files for: <span className="font-bold">{savedProjectTitle}</span></p>
                    <FileUploadPanel projectId={savedProjectId}
                      existingFiles={projects.find(p => p._id === savedProjectId)?.attachments || []}
                      onUploaded={(attachments) => setProjects(prev => prev.map(p => p._id === savedProjectId ? { ...p, attachments } : p))} />
                  </div>
                )}
              </>
            )}

            {/* TL, HR & Design, Developer — no create form, show message */}
            {!isAdmin && !CREATE_ROLES.includes(workerRole) && (
              <div className="flex flex-col items-center justify-center h-48 text-center text-gray-400">
                <FaLink className="text-4xl mb-3 text-purple-200" />
                <p className="text-sm">You can view and manage projects assigned to you on the right.</p>
              </div>
            )}
          </div>

          {/* ── RIGHT: Project Table ───────────────────────────────────────── */}
          <div className="bg-white p-4 rounded-xl shadow overflow-x-auto">
            <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-3">
              <h3 className="text-xl font-semibold text-purple-600">{isAdmin ? 'All Projects' : 'My Projects'}</h3>
              <input type="text" placeholder="Search by title..."
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="border px-4 py-2 rounded-lg w-full md:w-64 text-sm" />
            </div>

            <div className="overflow-y-auto max-h-[500px]">
              <table className="min-w-full border text-left text-sm">
                <thead className="bg-purple-100 text-purple-700 sticky top-0 z-10">
                  <tr>
                    <th className="p-2">Title</th>
                    <th className="p-2">Assigned</th>
                    <th className="p-2">Start</th>
                    <th className="p-2">End</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Files</th>
                    <th className="p-2">Chain</th>
                    <th className="p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && <tr><td colSpan="8" className="text-center py-8 text-gray-400">No projects found.</td></tr>}
                  {filtered.map(project => (
                    <tr key={project._id} className="border-t hover:bg-purple-50 transition">
                      <td className="p-2 font-medium">{project.title}</td>
                      <td className="p-2 text-xs">
                        {project.assignees?.length > 0
                          ? project.assignees.map(a => a.username).join(', ')
                          : project.assignedTo?.map(u => typeof u === 'object' ? u.username : 'Unknown').join(', ') || '—'}
                      </td>
                      <td className="p-2 whitespace-nowrap text-xs">{project.startDate?.slice(0,10) || '—'}</td>
                      <td className="p-2 whitespace-nowrap text-xs">{project.endDate?.slice(0,10) || '—'}</td>
                      <td className={`p-2 font-semibold text-xs ${project.status === 'Completed' ? 'text-green-600' : project.status === 'On Hold' ? 'text-blue-600' : 'text-yellow-600'}`}>
                        {project.status}
                      </td>
                      {/* Files */}
                      <td className="p-2">
                        <button onClick={() => { setAttachProject(project); setShowAttachModal(true); }}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">
                          <FaUpload /> {project.attachments?.length > 0 ? `${project.attachments.length} file(s)` : 'Files'}
                        </button>
                      </td>
                      {/* Chain */}
                      <td className="p-2">
                        {project.assignmentChain?.length > 0
                          ? <button onClick={() => { setChainProject(project); setShowChainModal(true); }}
                              className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium">
                              <FaLink /> View
                            </button>
                          : <span className="text-xs text-gray-400">—</span>}
                      </td>
                      {/* Actions */}
                      <td className="p-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          {isAdmin && (
                            <>
                              <button onClick={() => handleEdit(project)} className="text-blue-600 hover:text-blue-800"><FaEdit /></button>
                              <button onClick={() => handleDelete(project._id)} className="text-red-600 hover:text-red-800"><FaTrash /></button>
                            </>
                          )}
                          {FORWARD_ROLES.includes(workerRole) && project.status !== 'Completed' && (
                            <button onClick={() => openForwardModal(project)}
                              className="flex items-center gap-1 text-xs bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded-lg">
                              <FaUserPlus /> Forward
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── ATTACHMENT MODAL ─────────────────────────────────────────────────── */}
      {showAttachModal && attachProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowAttachModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Files</h3>
            <p className="text-sm text-gray-500 mb-4">"<span className="font-medium text-gray-700">{attachProject.title}</span>"</p>
            <FileUploadPanel projectId={attachProject._id} existingFiles={attachProject.attachments || []}
              onUploaded={(attachments) => {
                setProjects(prev => prev.map(p => p._id === attachProject._id ? { ...p, attachments } : p));
                setAttachProject(prev => ({ ...prev, attachments }));
              }} />
          </div>
        </div>
      )}

      {/* ── FORWARD MODAL ────────────────────────────────────────────────────── */}
      {showForwardModal && forwardProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 relative">
            <button onClick={() => setShowForwardModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Forward Project</h3>
            <p className="text-sm text-gray-500 mb-3">"<span className="font-medium text-gray-700">{forwardProject.title}</span>"</p>
            {forwardProject.assignmentChain?.length > 0 && (
              <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Current chain:</p>
                <ChainTrail chain={forwardProject.assignmentChain} />
                <div className="flex items-center gap-1 mt-1">
                  <FaChevronRight className="text-purple-300 text-xs" />
                  <span className="text-xs bg-purple-100 text-purple-700 border border-purple-300 px-2 py-0.5 rounded-full font-semibold">
                    {worker?.username} ({workerRole}) → ?
                  </span>
                </div>
              </div>
            )}
            <div className="border rounded-lg divide-y max-h-56 overflow-y-auto mb-4">
              {forwardWorkers.length === 0
                ? <p className="text-sm text-gray-400 p-4 text-center">No eligible workers.</p>
                : forwardWorkers.map(w => (
                  <label key={w._id} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-purple-50 transition ${forwardSelectedIds.includes(w._id) ? 'bg-purple-50' : ''}`}>
                    <input type="checkbox" checked={forwardSelectedIds.includes(w._id)}
                      onChange={() => setForwardSelectedIds(prev => prev.includes(w._id) ? prev.filter(x => x !== w._id) : [...prev, w._id])}
                      className="accent-purple-600" />
                    <span className="flex-1 text-sm font-medium">{w.username}</span>
                    <RoleBadge role={w.role} />
                  </label>
                ))}
            </div>
            {forwardSelectedIds.length > 0 && <p className="text-xs text-purple-600 mb-3">{forwardSelectedIds.length} selected</p>}
            <button onClick={handleForward} className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 rounded-lg">Forward Project</button>
          </div>
        </div>
      )}

      {/* ── CHAIN MODAL ──────────────────────────────────────────────────────── */}
      {showChainModal && chainProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 relative">
            <button onClick={() => setShowChainModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Assignment Chain</h3>
            <p className="text-sm text-gray-500 mb-4">"<span className="font-medium text-gray-700">{chainProject.title}</span>"</p>
            <div className="mb-4"><p className="text-xs text-gray-400 mb-2">Delegation path:</p><ChainTrail chain={chainProject.assignmentChain} /></div>
            <div>
              <p className="text-xs text-gray-400 mb-2">All people on this project:</p>
              <div className="space-y-2">
                {chainProject.assignees?.map((a, i) => (
                  <div key={i} className={`flex items-center gap-2 p-2 rounded-lg border ${a.userId?.toString() === workerId ? 'bg-purple-50 border-purple-300' : 'bg-gray-50 border-gray-200'}`}>
                    <span className="text-sm font-medium flex-1">{a.username}</span>
                    <RoleBadge role={a.role} />
                    <span className="text-xs text-gray-400">← {a.assignedBy}</span>
                    {a.userId?.toString() === workerId && <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">You</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfficeProjectManager;